import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import StatutBadge from './StatutBadge';
import { STATUT_LIST, statutLabel, personName, fmtDate } from './constants';

const EMPTY = { q: '', site_id: '', statut: '', activite_id: '', urgence: false, scope: '' };

export default function DemandesListe({ currentUser, refs, preset, onOpen, onEdit }) {
  const [filters, setFilters] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  // Filtre injecté depuis le tableau de bord
  useEffect(() => {
    if (!preset) return;
    setFilters({ ...EMPTY, ...preset, _ts: undefined });
  }, [preset]);

  const load = useCallback(async () => {
    setLoading(true);
    setDenied(false);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.site_id) params.site_id = filters.site_id;
      if (filters.statut) params.statut = filters.statut;
      if (filters.activite_id) params.activite_id = filters.activite_id;
      if (filters.urgence) params.urgence = 'true';
      if (filters.scope) params.scope = filters.scope;
      const { data } = await api.get('/maintenance/demandes', { params });
      setRows(data);
    } catch (err) {
      if (err.response?.status === 403) setDenied(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, filters.q ? 300 : 0); // léger debounce sur la recherche
    return () => clearTimeout(t);
  }, [load, filters.q]);

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const toggleScope = (s) => set({ scope: filters.scope === s ? '' : s });
  const toggleStatut = (s) => set({ statut: filters.statut === s ? '' : s });

  if (denied) {
    return <div className="table-container"><div className="table-empty">Vous n'avez pas accès aux demandes de maintenance. Demandez l'autorisation à un administrateur.</div></div>;
  }

  return (
    <>
      <div className="page-header-row">
        <div>
          <h2 className="page-title">Demandes de maintenance</h2>
          <p className="page-subtitle">{loading ? 'Chargement…' : `${rows.length} demande(s)`}</p>
        </div>
      </div>

      <div className="page-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher (titre, n°, réf.)"
          value={filters.q}
          onChange={(e) => set({ q: e.target.value })}
        />
        <select className="page-select" value={filters.site_id} onChange={(e) => set({ site_id: e.target.value })}>
          <option value="">Tous les sites</option>
          {refs.sites.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
        </select>
        <select className="page-select" value={filters.statut} onChange={(e) => set({ statut: e.target.value })}>
          <option value="">Tous les statuts</option>
          {STATUT_LIST.map((s) => <option key={s} value={s}>{statutLabel(s)}</option>)}
        </select>
        <select className="page-select" value={filters.activite_id} onChange={(e) => set({ activite_id: e.target.value })}>
          <option value="">Toutes les activités</option>
          {refs.activites.map((a) => <option key={a.id} value={a.id}>{a.nom}</option>)}
        </select>
      </div>

      <div className="mnt-chips-bar">
        <button className={`mnt-chip-btn ${filters.urgence ? 'active' : ''}`} onClick={() => set({ urgence: !filters.urgence })}>🔴 Urgentes</button>
        <button className={`mnt-chip-btn ${filters.scope === 'en_retard' ? 'active' : ''}`} onClick={() => toggleScope('en_retard')}>⏰ En retard</button>
        <button className={`mnt-chip-btn ${filters.statut === 'a_traiter' ? 'active' : ''}`} onClick={() => toggleStatut('a_traiter')}>📥 À traiter</button>
        <button className={`mnt-chip-btn ${filters.scope === 'mine' ? 'active' : ''}`} onClick={() => toggleScope('mine')}>👤 Mes demandes</button>
        {(filters.q || filters.site_id || filters.statut || filters.activite_id || filters.urgence || filters.scope) && (
          <button className="mnt-chip-btn" onClick={() => setFilters(EMPTY)}>✕ Réinitialiser</button>
        )}
      </div>

      {/* Bureau : tableau */}
      <div className="table-container mnt-table-desktop">
        <table>
          <thead>
            <tr>
              <th>N°</th><th>Titre</th><th>Site</th><th>Activité</th><th>Urg.</th>
              <th>Statut</th><th>Demandeur</th><th>Créée le</th><th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="table-empty">Chargement…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="table-empty">Aucune demande. Cliquez sur « + Nouvelle demande ».</td></tr>
            ) : rows.map((d) => (
              <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(d.id)}>
                <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{d.numero}</td>
                <td>{d.titre}</td>
                <td>{d.site_nom || '—'}</td>
                <td style={{ color: 'var(--text-secondary)' }}>{d.activite_nom || '—'}</td>
                <td>{d.urgence ? <span className="mnt-chip mnt-chip-urgence">URGENT</span> : ''}</td>
                <td><StatutBadge statut={d.statut} /></td>
                <td style={{ whiteSpace: 'nowrap' }}>{personName(d, 'demandeur_')}</td>
                <td style={{ whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{fmtDate(d.created_at)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button className="btn-icon" title="Ouvrir" onClick={() => onOpen(d.id)}>👁️</button>
                    <button className="btn-icon" title="Modifier" onClick={() => onEdit(d.id)}>✏️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile : cartes (aucun scroll horizontal) */}
      <div className="mnt-cards">
        {loading ? (
          <div className="table-container"><div className="table-empty">Chargement…</div></div>
        ) : rows.length === 0 ? (
          <div className="table-container"><div className="table-empty">Aucune demande. Cliquez sur « + Nouvelle demande ».</div></div>
        ) : rows.map((d) => (
          <button key={d.id} className="mnt-dcard" onClick={() => onOpen(d.id)}>
            <div className="mnt-dcard-top">
              <span className="mnt-dcard-num">{d.numero}</span>
              <StatutBadge statut={d.statut} />
              {d.urgence && <span className="mnt-chip mnt-chip-urgence">URGENT</span>}
            </div>
            <div className="mnt-dcard-title">{d.titre}</div>
            <div className="mnt-dcard-meta">
              <span>📍 {d.site_nom || '—'}</span>
              <span>🔧 {d.activite_nom || '—'}</span>
            </div>
            <div className="mnt-dcard-meta">
              <span>👤 {personName(d, 'demandeur_')}</span>
              <span>🗓️ {fmtDate(d.created_at)}</span>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}
